import { ApiClient, ApiRequestError } from '../api/client.js';
import type {
  ApiAuthenticatedData,
  ApiCatalogItem,
  ApiCatalogSearchData,
  ApiCsrfData,
  ApiFreshnessState,
  ApiLoginData,
  ApiLoginRequest,
  ApiPriceValue,
  ApiSupplierOffer,
  ApiSupplierOffersData,
  ApiTenantContext,
  ApiTotpRequest,
} from '../api/openapiTypes.js';
import { BACKEND_TENANT_ROLE_LABELS } from '../domain/models.js';
import type {
  AccessRequest,
  CanonicalCatalogItem,
  CatalogOffersResult,
  CatalogSearchResult,
  CommercialAccess,
  FreshnessState,
  LoginInput,
  LoginResult,
  MappingRecord,
  MoneyValue,
  PlatformSupplierIntegration,
  QuantityValue,
  SupplierOffer,
  SupportGrant,
  TenantAuthContext,
  TenantSupplierSummary,
  TenantUser,
} from '../domain/models.js';
import type { DataSotaRepository } from './contracts.js';

const freshnessLabels: Record<ApiFreshnessState, string> = {
  current: 'Актуально',
  aging: 'Давно не обновлялось',
  stale: 'Устарело',
  error: 'Ошибка источника',
  unknown: 'Неизвестно',
};

function mapFreshness(state: ApiFreshnessState): FreshnessState {
  return state === 'error' ? 'source_error' : state;
}

function mapTenantContext(context: ApiTenantContext): TenantAuthContext {
  return {
    user: {
      id: context.user.id,
      displayName: context.user.display_name,
      email: context.user.email,
    },
    tenant: {
      id: context.tenant.id,
      slug: context.tenant.slug,
      name: context.tenant.name,
    },
    tenantRole: context.tenant_role,
    tenantRoleLabel: BACKEND_TENANT_ROLE_LABELS[context.tenant_role],
    mfaVerified: context.mfa_verified,
  };
}

function displayUnit(unit: { code: string | null; name: string | null }): string {
  return unit.name ?? unit.code ?? '';
}

function mapPrice(value: ApiPriceValue): MoneyValue {
  return {
    amount: value.amount,
    currency: value.currency.code,
    state: value.state,
  };
}

function duplicateArticles(items: ApiCatalogItem[]): Set<string> {
  const counts = new Map<string, number>();
  for (const item of items) {
    if (!item.article) continue;
    counts.set(item.article, (counts.get(item.article) ?? 0) + 1);
  }
  return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([article]) => article));
}

export function mapCatalogItems(items: ApiCatalogItem[]): CanonicalCatalogItem[] {
  const duplicates = duplicateArticles(items);
  return items.map((item) => ({
    id: item.catalog_guid,
    article: item.article,
    name: item.name,
    brand: item.brand,
    collection: item.collection,
    unit: displayUnit(item.unit),
    texture: null,
    duplicateWarning: item.article && duplicates.has(item.article)
      ? 'Одинаковый артикул найден у нескольких канонических товаров'
      : undefined,
    warnings: [...item.warnings],
    supplierCount: 0,
    warehouseCount: 0,
    offers: [],
    offersState: 'not_loaded',
  }));
}

function freshnessDetail(offer: ApiSupplierOffer): string {
  const facts = [`Состояние источника: ${freshnessLabels[offer.freshness.state].toLowerCase()}.`];
  if (offer.freshness.source_as_of) facts.push(`Данные источника на ${offer.freshness.source_as_of}.`);
  if (offer.freshness.last_success_at) facts.push(`Последний успешный ingest: ${offer.freshness.last_success_at}.`);
  return facts.join(' ');
}

function quantity(amount: string | null, unit: string, state: QuantityValue['state'], expectedAt?: string | null): QuantityValue {
  return expectedAt === undefined ? { amount, unit, state } : { amount, unit, state, expectedAt };
}

function offerBase(offer: ApiSupplierOffer) {
  const unit = displayUnit(offer.supplier_product.unit);
  return {
    supplierId: offer.supplier.code,
    supplierName: offer.supplier.name,
    supplierSku: [offer.supplier_product.article, offer.supplier_product.external_product_id].filter(Boolean).join(' / '),
    purchase: mapPrice(offer.prices.purchase),
    rrp: mapPrice(offer.prices.rrp),
    freshness: mapFreshness(offer.freshness.state),
    freshnessLabel: freshnessLabels[offer.freshness.state],
    freshnessDetail: freshnessDetail(offer),
    observedAt: offer.freshness.observed_at,
    lastSuccessAt: offer.freshness.last_success_at ?? undefined,
    sourceAsOf: offer.freshness.source_as_of,
    unit,
  };
}

function mapOfferRows(offer: ApiSupplierOffer): SupplierOffer[] {
  const base = offerBase(offer);
  if (offer.inventory.response_state === 'rows_returned') {
    return offer.inventory.rows.map((row, index) => {
      const unit = row.rest.unit ?? base.unit;
      const free = row.free.state === 'expected_free'
        ? quantity(null, unit, 'expected_free')
        : quantity(row.free.quantity, unit, row.free.quantity === '0.000000' ? 'confirmed_zero' : 'reported');
      const expected = quantity(
        row.expected.quantity,
        unit,
        row.expected.state === 'not_expected'
          ? 'not_expected'
          : row.expected.quantity === '0.000000' ? 'confirmed_zero' : 'reported',
        row.expected.expected_at,
      );
      return {
        id: `${offer.supplier.code}-${offer.supplier_product.external_product_id}-${index}`,
        supplierId: base.supplierId,
        supplierName: base.supplierName,
        supplierSku: base.supplierSku,
        warehouse: row.warehouse.name ?? row.warehouse.external_id ?? 'Склад не указан',
        lot: row.lot.label ?? row.lot.external_id ?? undefined,
        purchase: base.purchase,
        rrp: base.rrp,
        free,
        expected,
        freshness: base.freshness,
        freshnessLabel: base.freshnessLabel,
        freshnessDetail: base.freshnessDetail,
        observedAt: base.observedAt,
        lastSuccessAt: base.lastSuccessAt,
        sourceAsOf: base.sourceAsOf,
      };
    });
  }

  if (offer.inventory.response_state === 'confirmed_zero' && offer.inventory.confirmed_zero) {
    return [{
      id: `${offer.supplier.code}-${offer.supplier_product.external_product_id}-confirmed-zero`,
      supplierId: base.supplierId,
      supplierName: base.supplierName,
      supplierSku: base.supplierSku,
      warehouse: 'Остатки поставщика',
      purchase: base.purchase,
      rrp: base.rrp,
      free: quantity('0.000000', base.unit, 'confirmed_zero'),
      expected: quantity(
        offer.inventory.aggregates.expected_free_quantity,
        base.unit,
        offer.inventory.aggregates.expected_free_quantity === null
          ? 'not_expected'
          : offer.inventory.aggregates.expected_free_quantity === '0.000000' ? 'confirmed_zero' : 'reported',
        null,
      ),
      freshness: base.freshness,
      freshnessLabel: base.freshnessLabel,
      freshnessDetail: `${base.freshnessDetail} Нулевой свободный остаток подтверждён Backend projection.`,
      observedAt: base.observedAt,
      lastSuccessAt: base.lastSuccessAt,
      sourceAsOf: base.sourceAsOf,
    }];
  }

  return [{
    id: `${offer.supplier.code}-${offer.supplier_product.external_product_id}-missing-inventory`,
    supplierId: base.supplierId,
    supplierName: base.supplierName,
    supplierSku: base.supplierSku,
    warehouse: 'Остатки не возвращены',
    purchase: base.purchase,
    rrp: base.rrp,
    free: quantity(null, base.unit, 'missing_not_returned'),
    expected: quantity(null, base.unit, 'missing_not_returned'),
    freshness: base.freshness,
    freshnessLabel: base.freshnessLabel,
    freshnessDetail: `${base.freshnessDetail} Остатки отсутствуют в ответе источника; это не подтверждённый ноль.`,
    observedAt: base.observedAt,
    lastSuccessAt: base.lastSuccessAt,
    sourceAsOf: base.sourceAsOf,
  }];
}

export function mapSupplierOffersData(data: ApiSupplierOffersData): CatalogOffersResult {
  const offers = data.offers.flatMap(mapOfferRows);
  const suppliers = new Set(data.offers.map((offer) => offer.supplier.code));
  const warehouses = new Set<string>();
  for (const offer of data.offers) {
    for (const row of offer.inventory.rows) {
      const key = row.warehouse.external_id ?? row.warehouse.name;
      if (key) warehouses.add(`${offer.supplier.code}:${key}`);
    }
  }
  return {
    catalogGuid: data.canonical_item.catalog_guid,
    supplierCount: suppliers.size,
    warehouseCount: warehouses.size,
    offers,
    state: 'loaded',
  };
}

export class ApiScopeUnavailableError extends Error {
  constructor(operation: string) {
    super(`${operation} is outside the accepted first Frontend API slice.`);
    this.name = 'ApiScopeUnavailableError';
  }
}

export class ApiDataSotaRepository implements DataSotaRepository {
  readonly mode = 'api' as const;
  private readonly client: ApiClient;
  private challengeToken: string | null = null;
  private csrfToken: string | null = null;
  private authContext: TenantAuthContext | null = null;

  constructor(client = new ApiClient()) {
    this.client = client;
  }

  async login(input: LoginInput): Promise<LoginResult> {
    const request: ApiLoginRequest = {
      tenant_slug: input.tenantSlug,
      email: input.email,
      password: input.password,
    };
    const response = await this.client.post<ApiLoginData>('/auth/login', request);
    if (response.data.state === 'mfa_required') {
      this.challengeToken = response.data.challenge_token;
      this.csrfToken = null;
      this.authContext = null;
      return { state: 'mfa_required', expiresAt: response.data.expires_at };
    }
    return this.acceptAuthenticated(response.data);
  }

  async verifyMfa(code: string): Promise<TenantAuthContext> {
    if (!this.challengeToken) throw new Error('No active MFA challenge is available in memory.');
    const request: ApiTotpRequest = { challenge_token: this.challengeToken, code };
    const response = await this.client.post<ApiAuthenticatedData>('/auth/mfa/totp', request);
    const accepted = this.acceptAuthenticated(response.data);
    return accepted.context;
  }

  async restoreAuthContext(): Promise<TenantAuthContext> {
    const response = await this.client.get<ApiTenantContext>('/auth/context');
    this.authContext = mapTenantContext(response.data);
    await this.rotateCsrf();
    return this.authContext;
  }

  async logout(): Promise<void> {
    try {
      if (!this.csrfToken) await this.rotateCsrf();
      await this.client.post<unknown>('/auth/logout', undefined, { 'X-CSRF-Token': this.csrfToken ?? '' });
    } finally {
      this.challengeToken = null;
      this.csrfToken = null;
      this.authContext = null;
    }
  }

  getCachedAuthContext(): TenantAuthContext | null {
    return this.authContext;
  }

  async searchCatalog(query: string): Promise<CatalogSearchResult> {
    const normalized = query.trim();
    const response = await this.client.get<ApiCatalogSearchData>(`/tenant/catalog/items?query=${encodeURIComponent(normalized)}`);
    return {
      query: normalized,
      items: mapCatalogItems(response.data.items),
      mockNotice: '',
    };
  }

  async loadCatalogOffers(catalogGuid: string): Promise<CatalogOffersResult> {
    try {
      const response = await this.client.get<ApiSupplierOffersData>(`/tenant/catalog/items/${encodeURIComponent(catalogGuid)}/supplier-offers`);
      return mapSupplierOffersData(response.data);
    } catch (error) {
      if (error instanceof ApiRequestError && (error.status === 404 || error.status === 503)) {
        return {
          catalogGuid,
          supplierCount: 0,
          warehouseCount: 0,
          offers: [],
          state: 'source_error',
          diagnostic: {
            message: error.status === 503
              ? 'Нет полной успешной подтверждённой проекции предложений. Это не подтверждённый нулевой остаток.'
              : 'Канонический товар больше не найден Backend API.',
            requestId: error.requestId,
            status: error.status,
          },
        };
      }
      throw error;
    }
  }

  listTenantSuppliers(): Promise<TenantSupplierSummary[]> { return Promise.reject(new ApiScopeUnavailableError('Tenant Suppliers')); }
  listPlatformSupplierIntegrations(): Promise<PlatformSupplierIntegration[]> { return Promise.reject(new ApiScopeUnavailableError('Platform Supplier Integrations')); }
  listMappings(): Promise<MappingRecord[]> { return Promise.reject(new ApiScopeUnavailableError('Mappings')); }
  listTenantUsers(): Promise<TenantUser[]> { return Promise.reject(new ApiScopeUnavailableError('Tenant Users')); }
  listAccessRequests(): Promise<AccessRequest[]> { return Promise.reject(new ApiScopeUnavailableError('Access Requests')); }
  listCommercialAccess(): Promise<CommercialAccess[]> { return Promise.reject(new ApiScopeUnavailableError('Commercial Access')); }
  listSupportGrants(): Promise<SupportGrant[]> { return Promise.reject(new ApiScopeUnavailableError('Support Grants')); }

  private acceptAuthenticated(data: ApiAuthenticatedData): { state: 'authenticated'; context: TenantAuthContext } {
    this.challengeToken = null;
    this.csrfToken = data.csrf_token;
    this.authContext = mapTenantContext(data.context);
    return { state: 'authenticated', context: this.authContext };
  }

  private async rotateCsrf(): Promise<void> {
    const response = await this.client.post<ApiCsrfData>('/auth/csrf');
    this.csrfToken = response.data.csrf_token;
  }
}
